import { useEffect, useState, useCallback } from 'react'
import { api, type Page } from '../api/client'
import type { Lead as BaseLead } from '../api/leads'
import { SavedViewsBar } from '../components/SavedViewsBar'
import { LeadBulkActionsBar } from '../components/LeadBulkActionsBar'
import { callCenterReportApi, type LeadOperationalStateRow } from '../api/callCenterReport'
import { leadStatusesApi, type LeadStatusDefinition } from '../api/leadStatuses'
import {
  ButtonLink, Button, Callout, Card, Checkbox, DataTable, EmptyState, FilterBar, LoadingState,
  Page as UiPage, PageHeader, Pagination, Select, StatusPill, Tabs, TextField, type PillTone,
} from '../components/ui'

// Extended interface for the list view — the API returns estimatedValue
// beyond the base Lead interface (same pattern as LeadDetailPage.tsx).
interface Lead extends BaseLead {
  estimatedValue?: number
}

const TYPE_LABELS: Record<string, string> = {
  person: 'Persona',
  company: 'Azienda',
}
const TYPE_COLORS: Record<string, string> = {
  person: 'info',
  company: 'secondary',
}
const STATUS_LABELS: Record<string, string> = {
  new: 'Nuovo',
  contacted: 'Contattato',
  qualified: 'Qualificato',
  converted: 'Convertito',
  lost: 'Perso',
}
const STATUS_COLORS: Record<string, string> = {
  new: 'primary',
  contacted: 'info',
  qualified: 'success',
  converted: 'dark',
  lost: 'danger',
}

// Nome del colore Bootstrap usato dai dati -> tono dello StatusPill.
const PILL_TONES: Record<string, PillTone> = {
  primary: 'primary',
  secondary: 'neutral',
  success: 'success',
  danger: 'error',
  warning: 'warning',
  info: 'info',
  dark: 'neutral',
}
function pillTone(color: string): PillTone {
  return PILL_TONES[color] ?? 'neutral'
}

const PAGE_SIZE = 25

function fmtDate(s: string): string {
  return new Date(s).toLocaleDateString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

// Humanized "time since arrival" (REQ-065-002), separate from the coarse
// agingBucket below (REQ-089-001): a precise relative-time string ("3 minuti
// fa", "2 ore fa", "5 giorni fa") rather than a day-range badge. Only the
// "received X ago" variant is implemented — "not contacted for" would need a
// LastContactedAt-style field that doesn't exist on Lead (out of scope, see
// TASK-0190).
function timeSinceArrival(createdAt: string): string {
  const diffMs = Date.now() - new Date(createdAt).getTime()
  const minutes = Math.floor(diffMs / (1000 * 60))
  if (minutes < 1) return 'pochi secondi fa'
  if (minutes < 60) return `${minutes} minut${minutes === 1 ? 'o' : 'i'} fa`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours} or${hours === 1 ? 'a' : 'e'} fa`
  const days = Math.floor(hours / 24)
  return `${days} giorn${days === 1 ? 'o' : 'i'} fa`
}

// Lead Aging (REQ-089-001) bucket boundaries. Note: the original requirement
// text overlapped at day 15 ("8-15 days" vs "15-30 days") — corrected here to
// non-overlapping 8-15 / 16-30, as documented in TASK-0152.
type AgingBucket = '0-1' | '2-3' | '4-7' | '8-15' | '16-30' | '31+'

const AGING_LABELS: Record<AgingBucket, string> = {
  '0-1': '0-1 giorni',
  '2-3': '2-3 giorni',
  '4-7': '4-7 giorni',
  '8-15': '8-15 giorni',
  '16-30': '16-30 giorni',
  '31+': 'oltre 30 giorni',
}

// Color intensifies monotonically with age, reusing Bootstrap semantic
// classes already used elsewhere in this file (STATUS_COLORS/TYPE_COLORS).
const AGING_COLORS: Record<AgingBucket, string> = {
  '0-1': 'success',
  '2-3': 'success',
  '4-7': 'warning',
  '8-15': 'warning',
  '16-30': 'danger',
  '31+': 'danger',
}

function agingBucket(createdAt: string): AgingBucket {
  const days = Math.floor(
    (Date.now() - new Date(createdAt).getTime()) / (1000 * 60 * 60 * 24),
  )
  if (days <= 1) return '0-1'
  if (days <= 3) return '2-3'
  if (days <= 7) return '4-7'
  if (days <= 15) return '8-15'
  if (days <= 30) return '16-30'
  return '31+'
}

export function LeadsPage() {
  // Applied filter state (triggers fetch)
  const [search, setSearch] = useState('')
  const [leadType, setLeadType] = useState('')
  const [status, setStatus] = useState('')
  // F-102: filtro per stato operativo / esito configurabile (codice).
  const [statusDetail, setStatusDetail] = useState('')
  const [page, setPage] = useState(1)
  // F-080 (REQ-087-001): ordinamento per punteggio ('' = ordine di sempre: priorità e data).
  const [sort, setSort] = useState<'' | 'score_desc' | 'score_asc'>('')

  // Pending form state
  const [pendingSearch, setPendingSearch] = useState('')
  const [pendingLeadType, setPendingLeadType] = useState('')
  const [pendingStatus, setPendingStatus] = useState('')
  const [pendingStatusDetail, setPendingStatusDetail] = useState('')
  // F-102: stati configurabili (per etichette e filtro). Un errore qui non blocca l'elenco.
  const [detailDefs, setDetailDefs] = useState<LeadStatusDefinition[]>([])
  useEffect(() => {
    leadStatusesApi.list().then(r => setDetailDefs(r.items)).catch(() => setDetailDefs([]))
  }, [])

  // F-066: selezione multipla per le operazioni sui lead (resta tra una pagina e l'altra, si azzera
  // cambiando i filtri o a operazione conclusa).
  const [selected, setSelected] = useState<Set<number>>(new Set())

  const [data, setData] = useState<Page<Lead> | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // F-084 (REQ-068-001): stato operativo derivato dei lead della pagina, per id. Un errore qui non blocca l'elenco.
  const [opStates, setOpStates] = useState<Record<number, LeadOperationalStateRow['state']>>({})

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const p = new URLSearchParams()
      if (search) p.set('search', search)
      if (status) p.set('status', status)
      if (statusDetail) p.set('statusDetail', statusDetail)
      if (leadType) p.set('leadType', leadType)
      if (sort) p.set('sort', sort)
      p.set('page', String(page))
      p.set('pageSize', String(PAGE_SIZE))
      const result = await api<Page<Lead>>(`/api/leads?${p}`)
      setData(result)
      try {
        const states = await callCenterReportApi.operationalStates(result.items.map(l => l.id).slice(0, 200))
        setOpStates(Object.fromEntries(states.items.map(r => [r.leadId, r.state])))
      } catch {
        setOpStates({})
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore')
    } finally {
      setLoading(false)
    }
  }, [search, leadType, status, statusDetail, page, sort])

  useEffect(() => {
    void load()
  }, [load])

  function handleFilter() {
    setSearch(pendingSearch)
    setLeadType(pendingLeadType)
    setStatus(pendingStatus)
    setStatusDetail(pendingStatusDetail)
    setPage(1)
    setSelected(new Set())
  }

  // F-080: un clic sull'intestazione "Score" passa da ordine abituale a decrescente, crescente e di nuovo abituale.
  function cycleScoreSort() {
    setSort(prev => (prev === '' ? 'score_desc' : prev === 'score_desc' ? 'score_asc' : ''))
    setPage(1)
  }

  function toggleSelected(id: number) {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function togglePageSelected(ids: number[], on: boolean) {
    setSelected(prev => {
      const next = new Set(prev)
      for (const id of ids) {
        if (on) next.add(id)
        else next.delete(id)
      }
      return next
    })
  }

  // F-063 (REQ-242-001): viste salvate = i filtri APPLICATI adesso (non quelli ancora in digitazione).
  function getSavedViewState() {
    return { filters: { search, leadType, status, statusDetail } }
  }

  function applySavedViewState(state: { filters?: Record<string, unknown> }) {
    const f = state.filters ?? {}
    const str = (k: string) => (typeof f[k] === 'string' ? (f[k] as string) : '')
    setPendingSearch(str('search')); setSearch(str('search'))
    setPendingLeadType(str('leadType')); setLeadType(str('leadType'))
    setPendingStatus(str('status')); setStatus(str('status'))
    setPendingStatusDetail(str('statusDetail')); setStatusDetail(str('statusDetail'))
    setPage(1)
    setSelected(new Set())
  }

  const items = data?.items ?? []
  const total = data?.total ?? 0
  const allOnPageSelected = items.length > 0 && items.every(l => selected.has(l.id))

  return (
    <UiPage>
      <PageHeader
        title="Tutti i lead"
        icon="person_search"
        count={total}
        actions={<ButtonLink to="/leads/create" variant="filled" icon="add">Nuovo lead</ButtonLink>}
        tabs={
          /* F-049: tabs between the lead list and the scheduled recontacts view */
          <Tabs
            aria-label="Elenchi lead"
            value="all"
            items={[
              { id: 'all', label: 'Tutti i lead', to: '/leads' },
              { id: 'recontacts', label: 'Ricontatti programmati', to: '/leads/recontacts' },
            ]}
          />
        }
      />

      <SavedViewsBar pageKey="leads" getState={getSavedViewState} applyState={applySavedViewState} />

      <FilterBar onSubmit={handleFilter}>
        <TextField
          type="text"
          label="Cerca..."
          labelHidden
          placeholder=" "
          value={pendingSearch}
          onChange={e => setPendingSearch(e.target.value)}
        />
        <Select
          label="Tipo"
          labelHidden
          value={pendingLeadType}
          onChange={e => setPendingLeadType(e.target.value)}
        >
          <option value="">Tutti i tipi</option>
          <option value="person">Persona</option>
          <option value="company">Azienda</option>
        </Select>
        <Select
          label="Stato"
          labelHidden
          value={pendingStatus}
          onChange={e => setPendingStatus(e.target.value)}
        >
          <option value="">Tutti gli stati</option>
          <option value="new">Nuovo</option>
          <option value="contacted">Contattato</option>
          <option value="qualified">Qualificato</option>
          <option value="converted">Convertito</option>
          <option value="lost">Perso</option>
        </Select>
        {/* F-102: filtro per stato operativo / esito configurabile; compare solo se ce ne sono. */}
        {detailDefs.length > 0 && (
          <Select
            label="Stato operativo / esito"
            labelHidden
            value={pendingStatusDetail}
            onChange={e => setPendingStatusDetail(e.target.value)}
          >
            <option value="">Ogni stato operativo / esito</option>
            {detailDefs.map(d => (
              <option key={d.code} value={d.code}>{d.label}</option>
            ))}
          </Select>
        )}
      </FilterBar>

      {error && <Callout tone="error">{error}</Callout>}

      {/* F-066: operazioni multiple (anteprima, conferma, avanzamento in background) */}
      <LeadBulkActionsBar
        selectedIds={[...selected]}
        onClear={() => setSelected(new Set())}
        onApplied={() => void load()}
      />

      {/* Table card */}
      <Card variant="outlined" padding="none">
        {loading ? (
          <LoadingState />
        ) : items.length === 0 ? (
          <EmptyState icon="filter_alt" title="Nessun lead trovato" />
        ) : (
          <>
            <DataTable variant="plain">
                <thead>
                  <tr>
                    <th>
                      <Checkbox
                        label={<span className="md-sr-only">Seleziona tutti i lead della pagina</span>}
                        aria-label="Seleziona tutti i lead della pagina"
                        checked={allOnPageSelected}
                        onChange={e => togglePageSelected(items.map(l => l.id), e.target.checked)}
                      />
                    </th>
                    <th>Nome</th>
                    <th>Email</th>
                    <th>Telefono</th>
                    <th>Tipo</th>
                    <th>Fonte</th>
                    <th>Stato</th>
                    <th>Stato operativo</th>
                    {detailDefs.length > 0 && <th>Esito / stato configurato</th>}
                    <th>
                      <Button
                        type="button"
                        variant="text"
                        size="sm"
                        title="Ordina per punteggio"
                        onClick={cycleScoreSort}
                      >
                        Score{sort === 'score_desc' ? ' ▼' : sort === 'score_asc' ? ' ▲' : ''}
                      </Button>
                    </th>
                    <th>Anzianità</th>
                    <th>Valore</th>
                    <th>Data ingresso</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(l => (
                    <tr key={l.id}>
                      <td>
                        <Checkbox
                          label={<span className="md-sr-only">{`Seleziona ${l.firstName ?? ''} ${l.lastName ?? ''}`.trim()}</span>}
                          aria-label={`Seleziona ${l.firstName ?? ''} ${l.lastName ?? ''}`.trim()}
                          checked={selected.has(l.id)}
                          onChange={() => toggleSelected(l.id)}
                        />
                      </td>
                      <td>
                        <a
                          href={`/leads/${l.id}`}
                          className="text-decoration-none md-typescale-emphasized-body-medium"
                        >
                          {l.firstName} {l.lastName}
                        </a>
                        {l.companyName && (
                          <>
                            <br />
                            <small className="text-muted">{l.companyName}</small>
                          </>
                        )}
                      </td>
                      <td>
                        <small>{l.email ?? '—'}</small>
                      </td>
                      <td>
                        <small>{l.phone ?? '—'}</small>
                      </td>
                      <td>
                        {l.leadType ? (
                          <StatusPill tone={pillTone(TYPE_COLORS[l.leadType] ?? 'secondary')}>
                            {(TYPE_LABELS[l.leadType] ?? l.leadType).toUpperCase()}
                          </StatusPill>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>
                        <small className="text-muted">{l.source ?? '—'}</small>
                      </td>
                      <td>
                        <StatusPill tone={pillTone(STATUS_COLORS[l.status] ?? 'secondary')}>
                          {STATUS_LABELS[l.status] ?? l.status}
                        </StatusPill>
                      </td>
                      <td>
                        {opStates[l.id] ? (
                          <small>
                            {opStates[l.id]!.label}
                            {opStates[l.id]!.finalOutcome && (
                              <span className="text-muted">
                                {' '}· {opStates[l.id]!.finalOutcome}
                                {opStates[l.id]!.lostReason ? ` (${opStates[l.id]!.lostReason})` : ''}
                              </span>
                            )}
                          </small>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      {detailDefs.length > 0 && (
                        <td>
                          {l.statusDetail ? (
                            <small>{detailDefs.find(d => d.code === l.statusDetail)?.label ?? l.statusDetail}</small>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                      )}
                      <td>
                        {l.score != null ? (
                          <StatusPill
                            tone={l.score >= 70 ? 'success' : l.score >= 40 ? 'warning' : 'neutral'}
                            title="Lead Score 0-100"
                          >
                            {l.score}
                          </StatusPill>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td>
                        {l.status !== 'converted' && l.status !== 'lost' ? (
                          <StatusPill tone={pillTone(AGING_COLORS[agingBucket(l.createdAt)])}>
                            {AGING_LABELS[agingBucket(l.createdAt)]}
                          </StatusPill>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="md-num">
                        {l.estimatedValue !== undefined && l.estimatedValue !== null
                          ? `€ ${l.estimatedValue.toLocaleString('it-IT', { minimumFractionDigits: 2 })}`
                          : '—'}
                      </td>
                      <td>
                        {fmtDate(l.createdAt)}
                        <br />
                        <small className="text-muted">{timeSinceArrival(l.createdAt)}</small>
                      </td>
                      <td>
                        <ButtonLink
                          to={`/leads/${l.id}/edit`}
                          variant="outlined"
                          size="sm"
                          icon="edit"
                          aria-label="Modifica lead"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
            </DataTable>
            {total > PAGE_SIZE && (
              <Pagination page={page} total={total} pageSize={PAGE_SIZE} onChange={setPage} />
            )}
          </>
        )}
      </Card>
    </UiPage>
  )
}
